import { Bar, CalendarMode, Row } from "PFApp/booking/types";
import React, { RefObject, useMemo } from "react";

import { getBarsType } from "../../../helpers";
import { useBookingRowsContext } from "../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";
import { useCalendarContext } from "../../context/calendar_context";
import { BAR_HEIGHT, ROW_PADDING, VERTICAL_BARS_MARGIN } from "../calendar";
import { setTimeToMidnight } from "../calendar.utils";
import BasicBar from "./basic_bar";
import { BookingBar } from "./booking_bar";
import { EngagementBar } from "./engagement_bar";
import { RoleBar } from "./role_bar";

export const getRoleBookings = (activityId: number, bars: Bar[][]): number[] =>
  bars.flatMap((barsRow) => barsRow.filter((bar) => bar.activityId === activityId).map(({ id }) => id!));

const getBarComponent = (type) =>
  ({
    engagement: EngagementBar,
    role: RoleBar,
    booking: BookingBar
  }[type] || BasicBar);

type BarsProps = {
  row: Row;
  rowRef: RefObject<HTMLDivElement>;
  startDate: string;
  endDate: string;
  onLoadingChange: (value: boolean) => void;
  draggedBookingId: number | null;
  onBarDragStart: (value: number) => void;
  onBarDragEnd: () => void;
  offsetTop?: number;
  shouldOverlap: boolean;
  titleDisabled?: boolean;
};

const Bars = ({
  startDate,
  endDate,
  rowRef,
  row,
  onLoadingChange,
  draggedBookingId,
  onBarDragStart,
  onBarDragEnd,
  offsetTop = 0,
  shouldOverlap,
  titleDisabled
}: BarsProps): JSX.Element => {
  const { dayWidth, days } = useCalendarContext();
  const { calendarMode } = useBookingsOverviewContext();
  const { barsByRowKey, allBookingsModeRowsState } = useBookingRowsContext();
  const { type, key, original } = row;
  const barsType = getBarsType(type, calendarMode);
  const bars = barsByRowKey[key] || [];
  const isAllBookingsModeEnabled = !!allBookingsModeRowsState[key];

  const formattedBars = useMemo(
    () =>
      bars
        .map((barsRow, index) =>
          barsRow.map((bar) => {
            const barStart = setTimeToMidnight(new Date(bar.start));
            const barEnd = setTimeToMidnight(new Date(bar.end));
            const { left: leftStart = 0 } =
              days.find(({ date }) => date.getTime() === barStart.getTime()) || {};
            const { left: leftEnd = 0, width: widthEnd = 0 } =
              days.find(({ date }) => date.getTime() === barEnd.getTime()) || {};
            const width = leftEnd + widthEnd - leftStart;

            const roleBookingsIds = bar.activityId ? getRoleBookings(bar.activityId, bars) : 1;

            return {
              ...bar,
              left: leftStart,
              width,
              ...(!shouldOverlap && {
                top: index * BAR_HEIGHT + offsetTop + ROW_PADDING + VERTICAL_BARS_MARGIN * index
              }),
              roleBookingsIds
            };
          })
        )
        .flat()
        // to hide booking bars right after allBookingsMode is OFF, without waiting for data to be refetched
        .filter(
          ({ isRelatedToParentRowActivity }) =>
            calendarMode !== CalendarMode.Projects ||
            barsType !== "booking" ||
            isAllBookingsModeEnabled ||
            isRelatedToParentRowActivity
        ),
    [bars, dayWidth, shouldOverlap, offsetTop, isAllBookingsModeEnabled]
  );

  return (
    <>
      {formattedBars.map((bar) => {
        const { start, end, id } = bar;
        const BarComponent = getBarComponent(barsType);

        return (
          <BarComponent
            rowRef={rowRef}
            key={`${key}-${start}-${end}-${id || ""}`}
            original={original}
            startDate={startDate}
            endDate={endDate}
            onLoadingChange={onLoadingChange}
            onBarDragStart={onBarDragStart}
            onBarDragEnd={onBarDragEnd}
            disabled={!!draggedBookingId && draggedBookingId !== id}
            titleDisabled={titleDisabled}
            isAllBookingsModeEnabled={isAllBookingsModeEnabled}
            row={row}
            {...bar}
          />
        );
      })}
    </>
  );
};

export default Bars;
